// The Ginastee art document (`ginastee.art/1`): what the editor reads and writes, what an
// art revision stores and what the AI generates. A document holds pages (a single post has
// one; carousels and Stories will hold several) of layers painted bottom to top.
// Coordinates are document pixels (a 4:5 post is 1080 × 1350): x/y are the top-left of the
// unrotated box, rotation is in degrees around the box center. Ids are created by whoever
// creates the element (the editor for manual edits, the server for AI output) and stay
// stable across revisions, so a scoped AI edit and the history can address them.
export const ART_SCHEMA = 'ginastee.art/1';

export const ASPECT_RATIOS = {
  '1:1': { width: 1080, height: 1080 },
  '4:5': { width: 1080, height: 1350 },
  '3:4': { width: 1080, height: 1440 },
  '9:16': { width: 1080, height: 1920 },
};

// The formats that are arts, the ratios each accepts (the first is the default), how many
// pages a document may hold and whether its pages take videos (2.13.0: carousels only).
// Stories are prepared, not offered yet.
export const ART_FORMATS = {
  'instagram::post': { ratios: ['4:5', '1:1', '3:4'], maxPages: 1, video: false },
  'instagram::carousel': { ratios: ['4:5', '1:1', '3:4'], maxPages: 10, video: true },
  'instagram::stories': { ratios: ['9:16'], maxPages: 10, video: false },
};
export const isArtFormat = format => Boolean(ART_FORMATS[format]);
export const formatRatios = format => ART_FORMATS[format]?.ratios || ART_FORMATS['instagram::post'].ratios;
export const takesVideo = format => !!ART_FORMATS[format]?.video;

export const LAYER_TYPES = ['text', 'shape', 'image', 'icon', 'video'];
export const SHAPES = ['rect', 'ellipse', 'triangle', 'diamond', 'star', 'hexagon', 'line', 'arrow'];
// A video's outline (2.13.0): the shapes that close.
export const VIDEO_MASKS = ['rect', 'ellipse', 'triangle', 'diamond', 'star', 'hexagon'];
// Instagram plays a carousel video of 3 to 60 s: a page's clips fit in 60 s, and a page
// shorter than 3 s holds its last frame on export. A clip lasts at least half a second.
export const VIDEO_LIMITS = { pageSeconds: 60, minPageSeconds: 3, minClipSeconds: 0.5, sourceSeconds: [0.1, 900], perPage: 8 };
// Stable roles. Templates and the AI address layers by them: a template swap moves the
// headline into the new headline, and a chat edit can name "the title".
export const TEXT_BINDINGS = ['headline', 'subheadline', 'body', 'list', 'cta', 'handle', 'badge', 'quote', 'author', 'stat', 'source'];
// `avatar` (contract 2.10.0) is the creator's Instagram picture, filled in from the brand kit.
export const BINDINGS = [...TEXT_BINDINGS, 'photo', 'logo', 'avatar', 'decoration'];
export const TEXT_ALIGNS = ['left', 'center', 'right'];
export const TEXT_TRANSFORMS = ['none', 'uppercase'];
export const HIGHLIGHT_MODES = ['box', 'lines'];
export const IMAGE_FITS = ['cover', 'contain'];
// `asset`: a creator's private upload (`sas-` id, signed `url` filled in on read).
// `library`: a Ginastee-hosted image (template photos), public `url`.
// `placeholder` (contract 2.10.0): an empty place for an image, as Canva's frames: no `url`,
// painted as a drawn landscape (a silhouette for an `avatar`) until a photo replaces it.
export const IMAGE_SOURCES = ['asset', 'library', 'placeholder'];
export const isPlaceholderSource = source => source?.kind === 'placeholder';

export const LIMITS = {
  pages: 10,
  layers: 80,
  textLength: 2000,
  size: [4, 8000],
  position: [-8000, 8000],
  rotation: [-360, 360],
  fontSize: [8, 480],
  lineHeight: [0.7, 3],
  letterSpacing: [-0.2, 1],
  strokeWidth: [0, 120],
  radius: [0, 2000],
  padding: [0, 200],
  shadowBlur: [0, 200],
  shadowOffset: [-400, 400],
  zoom: [1, 5],
  adjust: [-100, 100],
  blur: [0, 40],
  iconStroke: [0.5, 4],
  gradientStops: [2, 5],
  effects: 8,
  spread: [-200, 200],
  effectBlur: [0, 1000],
};

export const ID_PREFIX = { page: 'spg', layer: 'sly' };
export const ID_PATTERN = { page: /^spg-[0-9a-z]{7,10}$/, layer: /^sly-[0-9a-z]{7,10}$/, asset: /^sas-[0-9a-z]{7,10}$/ };
const HEX = /^#[0-9a-f]{6}$/;

const ALPHABET = '0123456789abcdefghijklmnopqrstuvwxyz';
export function newId(prefix) {
  const bytes = new Uint8Array(8);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) crypto.getRandomValues(bytes);
  else for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  return `${prefix}-${Array.from(bytes, byte => ALPHABET[byte % ALPHABET.length]).join('')}`;
}

export const clamp = (value, [min, max], fallback = min) => {
  const number = Number(value);
  return Number.isFinite(number) ? Math.min(max, Math.max(min, number)) : fallback;
};
const round = (value, digits = 2) => Math.round(value * 10 ** digits) / 10 ** digits;
const pick = (value, options, fallback = options[0]) => (options.includes(value) ? value : fallback);
const bool = (value, fallback = false) => (typeof value === 'boolean' ? value : fallback);
const text = (value, fallback = null, max = 120) => (typeof value === 'string' && value.trim() ? value.slice(0, max) : fallback);

export function normalizeColor(value, fallback = '#000000') {
  if (typeof value !== 'string') return fallback;
  let color = value.trim().toLowerCase();
  if (/^#[0-9a-f]{3}$/.test(color)) color = `#${[...color.slice(1)].map(c => c + c).join('')}`;
  if (/^#[0-9a-f]{8}$/.test(color)) color = color.slice(0, 7);
  return HEX.test(color) ? color : fallback;
}
export const isColor = value => typeof value === 'string' && HEX.test(value);

// A paint (what fills a page, a shape or a text): a solid color (with an `opacity` when it
// is see-through), a linear gradient (angle in CSS degrees: 0 = to top, clockwise), a
// radial gradient (from the center out to the corners) or, for shapes and texts, a photo
// masked by them (framed like an image layer, with the same adjustments). Gradient stops
// may fade (`opacity`), so a photo can darken toward its text.
export const PAINT_TYPES = ['solid', 'linear', 'radial', 'image'];
export const GRADIENT_TYPES = ['linear', 'radial'];
export function normalizePaint(fill, { types = ['solid', 'linear', 'radial'], fallback = { type: 'solid', color: '#ffffff' } } = {}) {
  if (!fill || typeof fill !== 'object') return fallback;
  if (fill.type === 'image') {
    const source = types.includes('image') ? normalizeSource(fill.source) : null;
    if (!source) return fallback;
    return { type: 'image', source, fit: pick(fill.fit, IMAGE_FITS), focus_x: round(clamp(fill.focus_x, [0, 1], 0.5), 3), focus_y: round(clamp(fill.focus_y, [0, 1], 0.5), 3),
      zoom: round(clamp(fill.zoom, LIMITS.zoom, 1)), adjust: normalizeAdjust(fill.adjust) };
  }
  if (GRADIENT_TYPES.includes(fill.type)) {
    const stops = (Array.isArray(fill.stops) ? fill.stops : [])
      .map(stop => ({ offset: round(clamp(stop?.offset, [0, 1], 0), 4), color: normalizeColor(stop?.color, null), opacity: round(clamp(stop?.opacity, [0, 1], 1)) }))
      .filter(stop => stop.color)
      .sort((a, b) => a.offset - b.offset)
      .slice(0, LIMITS.gradientStops[1]);
    if (stops.length < LIMITS.gradientStops[0]) return stops.length ? { type: 'solid', color: stops[0].color } : fallback;
    return fill.type === 'radial' && types.includes('radial') ? { type: 'radial', stops } : { type: 'linear', angle: round(clamp(fill.angle, [0, 360], 180)), stops };
  }
  const color = normalizeColor(fill.color, null);
  if (!color) return fallback;
  const opacity = round(clamp(fill.opacity, [0, 1], 1));
  return opacity < 1 ? { type: 'solid', color, opacity } : { type: 'solid', color };
}
// The page background: a color or a gradient.
export const normalizeFill = (fill, fallback = { type: 'solid', color: '#ffffff' }) => normalizePaint(fill, { fallback });

// Effects (Figma's): a layer stacks up to 8, painted in order. Shadows are cast by the
// layer's silhouette (outside it, or inside for an inner shadow), spread grows (or, below 0,
// shrinks) it; glass blurs what is behind the layer inside its silhouette and lets the
// layer's own paint through at `opacity`, with a light `edge`; blur softens the layer;
// texture lays film grain over it. Sizes are document pixels.
export const EFFECT_TYPES = ['drop_shadow', 'inner_shadow', 'glass', 'layer_blur', 'texture'];
export const EFFECT_DEFAULTS = {
  drop_shadow: { color: '#000000', opacity: 0.25, x: 0, y: 12, blur: 32, spread: 0, behind: false },
  inner_shadow: { color: '#000000', opacity: 0.25, x: 0, y: 8, blur: 16, spread: 0 },
  glass: { blur: 24, opacity: 0.25, edge: 0.5 },
  layer_blur: { radius: 8 },
  texture: { amount: 0.3, size: 2 },
};
export function normalizeEffect(effect) {
  if (!effect || typeof effect !== 'object' || !EFFECT_TYPES.includes(effect.type)) return null;
  const d = EFFECT_DEFAULTS[effect.type];
  const visible = bool(effect.visible, true);
  if (effect.type === 'drop_shadow' || effect.type === 'inner_shadow') {
    const shadow = { type: effect.type, visible, color: normalizeColor(effect.color, d.color), opacity: round(clamp(effect.opacity, [0, 1], d.opacity)),
      x: round(clamp(effect.x, LIMITS.shadowOffset, d.x)), y: round(clamp(effect.y, LIMITS.shadowOffset, d.y)),
      blur: round(clamp(effect.blur, LIMITS.effectBlur, d.blur)), spread: round(clamp(effect.spread, LIMITS.spread, d.spread)) };
    return effect.type === 'drop_shadow' ? { ...shadow, behind: bool(effect.behind, d.behind) } : shadow;
  }
  if (effect.type === 'glass') return { type: 'glass', visible, blur: round(clamp(effect.blur, LIMITS.effectBlur, d.blur)), opacity: round(clamp(effect.opacity, [0, 1], d.opacity)), edge: round(clamp(effect.edge, [0, 1], d.edge)) };
  if (effect.type === 'layer_blur') return { type: 'layer_blur', visible, radius: round(clamp(effect.radius, LIMITS.effectBlur, d.radius)) };
  return { type: 'texture', visible, amount: round(clamp(effect.amount, [0, 1], d.amount)), size: round(clamp(effect.size, [1, 8], d.size), 1) };
}
export const normalizeEffects = effects => (Array.isArray(effects) ? effects.map(normalizeEffect).filter(Boolean).slice(0, LIMITS.effects) : []);
export const createEffect = type => normalizeEffect({ type, ...EFFECT_DEFAULTS[type] });
// A text shadow from before effects (`style.shadow`) becomes the layer's drop shadow.
const legacyShadow = shadow => (shadow && typeof shadow === 'object'
  ? [{ type: 'drop_shadow', color: shadow.color, opacity: shadow.opacity ?? 0.35, x: shadow.x ?? 0, y: shadow.y ?? 8, blur: shadow.blur ?? 24, spread: 0, behind: true }] : []);

const normalizeHighlight = highlight => (highlight && typeof highlight === 'object' ? {
  color: normalizeColor(highlight.color, '#ffffff'),
  mode: pick(highlight.mode, HIGHLIGHT_MODES),
  padding: round(clamp(highlight.padding, LIMITS.padding, 24)),
  radius: round(clamp(highlight.radius, LIMITS.radius, 16)),
} : null);

export function normalizeTextStyle(style = {}) {
  return {
    font_family: text(style.font_family, 'Inter', 80),
    font_weight: Math.round(clamp(style.font_weight, [100, 900], 400) / 100) * 100,
    italic: bool(style.italic),
    font_size: round(clamp(style.font_size, LIMITS.fontSize, 48), 1),
    line_height: round(clamp(style.line_height, LIMITS.lineHeight, 1.2)),
    letter_spacing: round(clamp(style.letter_spacing, LIMITS.letterSpacing, 0), 3),
    align: pick(style.align, TEXT_ALIGNS),
    transform: pick(style.transform, TEXT_TRANSFORMS),
    underline: bool(style.underline),
    color: normalizeColor(style.color, '#111111'),
    // Paints the text instead of `color` (a see-through color, a gradient, a photo); `color`
    // stays its ink for contrast checks and plain renderers.
    fill: style.fill ? normalizePaint(style.fill, { types: PAINT_TYPES, fallback: null }) : null,
    highlight: normalizeHighlight(style.highlight),
  };
}

const normalizeAdjust = (adjust = {}) => ({
  brightness: Math.round(clamp(adjust.brightness, LIMITS.adjust, 0)),
  contrast: Math.round(clamp(adjust.contrast, LIMITS.adjust, 0)),
  saturation: Math.round(clamp(adjust.saturation, LIMITS.adjust, 0)),
  blur: round(clamp(adjust.blur, LIMITS.blur, 0), 1),
  grayscale: bool(adjust.grayscale),
});

function normalizeSource(source) {
  if (!source || typeof source !== 'object') return null;
  const kind = pick(source.kind, IMAGE_SOURCES, null);
  if (!kind) return null;
  const url = kind !== 'placeholder' && typeof source.url === 'string' && source.url ? source.url : null;
  const assetId = kind === 'asset' && typeof source.asset_id === 'string' ? source.asset_id : null;
  if (kind === 'asset' && !assetId) return null;
  if (kind === 'library' && !url) return null;
  return { kind, asset_id: assetId, url, width: Math.round(clamp(source.width, [1, 20000], 1000)), height: Math.round(clamp(source.height, [1, 20000], 1000)) };
}

// A video (2.13.0) is always the creator's upload (`art_video`, `sas-` id, signed `url` on
// read) and knows its whole length (`duration`, seconds).
function normalizeVideoSource(source) {
  if (!source || typeof source !== 'object' || source.kind !== 'asset' || typeof source.asset_id !== 'string' || !source.asset_id) return null;
  return { kind: 'asset', asset_id: source.asset_id, url: typeof source.url === 'string' && source.url ? source.url : null,
    width: Math.round(clamp(source.width, [1, 20000], 1080)), height: Math.round(clamp(source.height, [1, 20000], 1920)),
    duration: round(clamp(source.duration, VIDEO_LIMITS.sourceSeconds, 1), 3) };
}

// The part of the source a clip plays (`trim`, seconds in the file) and when it starts on
// the page (`at`): at least half a second, inside the file, ending by the page's 60 s.
export function normalizeClip({ trim, at } = {}, duration) {
  const { pageSeconds, minClipSeconds } = VIDEO_LIMITS;
  const shortest = Math.min(minClipSeconds, duration);
  const start = round(clamp(trim?.start, [0, Math.max(0, duration - shortest)], 0), 3);
  const end = round(clamp(trim?.end, [start + shortest, Math.min(duration, start + pageSeconds)], Math.min(duration, start + pageSeconds)), 3);
  return { trim: { start, end }, at: round(clamp(at, [0, Math.max(0, pageSeconds - (end - start))], 0), 3) };
}

// A shape takes any paint, a photo included (clipped to its outline).
export const normalizeShapeFill = (fill, fallback) => normalizePaint(fill, { types: PAINT_TYPES, fallback });

function normalizeBase(layer, type) {
  return {
    id: typeof layer.id === 'string' && ID_PATTERN.layer.test(layer.id) ? layer.id : newId(ID_PREFIX.layer),
    type,
    name: text(layer.name),
    binding: BINDINGS.includes(layer.binding) ? layer.binding : null,
    x: round(clamp(layer.x, LIMITS.position, 0)),
    y: round(clamp(layer.y, LIMITS.position, 0)),
    width: round(clamp(layer.width, LIMITS.size, 100)),
    height: round(clamp(layer.height, LIMITS.size, 100)),
    rotation: round(clamp(layer.rotation, LIMITS.rotation, 0)),
    opacity: round(clamp(layer.opacity, [0, 1], 1)),
    visible: bool(layer.visible, true),
    locked: bool(layer.locked),
    effects: normalizeEffects(layer.effects ?? (type === 'text' ? legacyShadow(layer.style?.shadow) : [])),
  };
}

// Unknown layer types (from a newer schema) and broken images are dropped, never guessed.
export function normalizeLayer(layer) {
  if (!layer || typeof layer !== 'object' || !LAYER_TYPES.includes(layer.type)) return null;
  const base = normalizeBase(layer, layer.type);
  if (layer.type === 'text') {
    return { ...base, text: typeof layer.text === 'string' ? layer.text.slice(0, LIMITS.textLength) : '', style: normalizeTextStyle(layer.style) };
  }
  if (layer.type === 'shape') {
    const shape = pick(layer.shape, SHAPES);
    const stroke = layer.stroke && typeof layer.stroke === 'object'
      ? { color: normalizeColor(layer.stroke.color, '#111111'), width: round(clamp(layer.stroke.width, LIMITS.strokeWidth, 4)) } : null;
    return { ...base, shape,
      fill: shape === 'line' ? null : (layer.fill === null ? null : normalizeShapeFill(layer.fill, { type: 'solid', color: '#111111' })),
      stroke: shape === 'line' ? (stroke || { color: '#111111', width: 6 }) : stroke,
      corner_radius: round(clamp(layer.corner_radius, LIMITS.radius, 0)) };
  }
  if (layer.type === 'image') {
    const source = normalizeSource(layer.source);
    if (!source) return null;
    return { ...base, source, fit: pick(layer.fit, IMAGE_FITS), focus_x: round(clamp(layer.focus_x, [0, 1], 0.5), 3), focus_y: round(clamp(layer.focus_y, [0, 1], 0.5), 3),
      zoom: round(clamp(layer.zoom, LIMITS.zoom, 1)), corner_radius: round(clamp(layer.corner_radius, LIMITS.radius, 0)),
      flip_x: bool(layer.flip_x), flip_y: bool(layer.flip_y), adjust: normalizeAdjust(layer.adjust) };
  }
  if (layer.type === 'video') {
    const source = normalizeVideoSource(layer.source);
    if (!source) return null;
    return { ...base, source, ...normalizeClip(layer, source.duration), muted: bool(layer.muted), volume: round(clamp(layer.volume, [0, 1], 1)),
      mask: pick(layer.mask, VIDEO_MASKS), fit: pick(layer.fit, IMAGE_FITS), focus_x: round(clamp(layer.focus_x, [0, 1], 0.5), 3), focus_y: round(clamp(layer.focus_y, [0, 1], 0.5), 3),
      zoom: round(clamp(layer.zoom, LIMITS.zoom, 1)), corner_radius: round(clamp(layer.corner_radius, LIMITS.radius, 0)),
      flip_x: bool(layer.flip_x), flip_y: bool(layer.flip_y), adjust: normalizeAdjust(layer.adjust) };
  }
  return { ...base, icon: text(layer.icon, 'tabler:sparkles', 80), color: normalizeColor(layer.color, '#111111'), stroke_width: round(clamp(layer.stroke_width, LIMITS.iconStroke, 2)) };
}

export function normalizePage(page) {
  const source = page && typeof page === 'object' ? page : {};
  return {
    id: typeof source.id === 'string' && ID_PATTERN.page.test(source.id) ? source.id : newId(ID_PREFIX.page),
    name: text(source.name),
    background: normalizeFill(source.background),
    layers: (Array.isArray(source.layers) ? source.layers : []).map(normalizeLayer).filter(Boolean).slice(0, LIMITS.layers),
  };
}

// Brings any input (an AI answer, a template, an older draft) to a valid document of the
// format: known values, numbers in range, unique ids. The page count is capped by the format.
export function normalizeDocument(input, { format, aspectRatio } = {}) {
  const source = input && typeof input === 'object' ? input : {};
  const kind = isArtFormat(source.format) ? source.format : isArtFormat(format) ? format : 'instagram::post';
  const ratios = formatRatios(kind);
  const ratio = ratios.includes(aspectRatio) ? aspectRatio : ratios.includes(source.aspect_ratio) ? source.aspect_ratio : ratios[0];
  const size = ASPECT_RATIOS[ratio];
  const pages = (Array.isArray(source.pages) && source.pages.length ? source.pages : [{}]).slice(0, ART_FORMATS[kind].maxPages).map(normalizePage);
  const seen = new Set();
  pages.forEach(page => {
    // Videos only where the format plays them, at most 8 per page.
    let videos = 0;
    page.layers = page.layers.filter(layer => layer.type !== 'video' || (takesVideo(kind) && ++videos <= VIDEO_LIMITS.perPage));
    if (seen.has(page.id)) page.id = newId(ID_PREFIX.page);
    seen.add(page.id);
    page.layers.forEach(layer => {
      if (seen.has(layer.id)) layer.id = newId(ID_PREFIX.layer);
      seen.add(layer.id);
    });
  });
  return { schema: ART_SCHEMA, format: kind, aspect_ratio: ratio, width: size.width, height: size.height, pages };
}

// A video layer (2.13.0): an uploaded `art_video`, a clip of at least half a second inside
// the file, ending by the page's 60 s, in a closed mask, in a format that plays videos.
function validateVideo(layer, path, format, add) {
  const number = value => typeof value === 'number' && Number.isFinite(value);
  if (!takesVideo(format)) add(`${path}.type`, 'videos are carousel pages only');
  const source = layer.source || {};
  if (source.kind !== 'asset') add(`${path}.source.kind`, 'must be asset');
  if (!ID_PATTERN.asset.test(source.asset_id || '')) add(`${path}.source.asset_id`, 'must match sas-');
  if (!number(source.duration) || source.duration < VIDEO_LIMITS.sourceSeconds[0] || source.duration > VIDEO_LIMITS.sourceSeconds[1]) add(`${path}.source.duration`, 'must be 0.1 to 900 seconds');
  const { start, end } = layer.trim || {};
  if (!number(start) || !number(end) || start < 0 || end - start < VIDEO_LIMITS.minClipSeconds - 1e-3 || (number(source.duration) && end > source.duration + 0.05)) {
    add(`${path}.trim`, 'must be a clip of at least 0.5 s inside the video');
  }
  if (!number(layer.at) || layer.at < 0) add(`${path}.at`, 'must be 0 or more');
  else if (number(start) && number(end) && layer.at + (end - start) > VIDEO_LIMITS.pageSeconds + 1e-3) add(`${path}.at`, 'the clip must end by the page\'s 60 s');
  if (!VIDEO_MASKS.includes(layer.mask)) add(`${path}.mask`, 'is unknown');
  if (!number(layer.volume) || layer.volume < 0 || layer.volume > 1) add(`${path}.volume`, 'must be 0 to 1');
}

// What the server checks before storing a document (422 with these paths in `errors`).
// The editor only ever writes documents that pass it.
export function validateDocument(doc) {
  const errors = [];
  const add = (path, message) => errors.push({ path, message });
  if (!doc || typeof doc !== 'object') { add('document', 'must be an object'); return errors; }
  if (doc.schema !== ART_SCHEMA) add('document.schema', `must be ${ART_SCHEMA}`);
  if (!isArtFormat(doc.format)) add('document.format', 'is not an art format');
  else if (!formatRatios(doc.format).includes(doc.aspect_ratio)) add('document.aspect_ratio', 'is not offered for this format');
  const size = ASPECT_RATIOS[doc.aspect_ratio];
  if (size && (doc.width !== size.width || doc.height !== size.height)) add('document.width', 'must match the aspect ratio');
  if (!Array.isArray(doc.pages) || !doc.pages.length) { add('document.pages', 'needs at least one page'); return errors; }
  if (isArtFormat(doc.format) && doc.pages.length > ART_FORMATS[doc.format].maxPages) add('document.pages', 'has more pages than the format allows');
  const ids = new Set();
  const unique = (id, path) => { if (ids.has(id)) add(path, 'is repeated'); ids.add(id); };
  doc.pages.forEach((page, p) => {
    const at = `document.pages.${p}`;
    if (!ID_PATTERN.page.test(page?.id || '')) add(`${at}.id`, 'must match spg-');
    unique(page?.id, `${at}.id`);
    if (page?.background && !['solid', ...GRADIENT_TYPES].includes(page.background.type)) add(`${at}.background.type`, 'must be a color or a gradient');
    if (!Array.isArray(page?.layers)) { add(`${at}.layers`, 'must be a list'); return; }
    if (page.layers.length > LIMITS.layers) add(`${at}.layers`, `holds more than ${LIMITS.layers} layers`);
    if (page.layers.filter(layer => layer?.type === 'video').length > VIDEO_LIMITS.perPage) add(`${at}.layers`, `holds more than ${VIDEO_LIMITS.perPage} videos`);
    page.layers.forEach((layer, l) => {
      const path = `${at}.layers.${l}`;
      if (!LAYER_TYPES.includes(layer?.type)) { add(`${path}.type`, 'is unknown'); return; }
      if (!ID_PATTERN.layer.test(layer.id || '')) add(`${path}.id`, 'must match sly-');
      unique(layer.id, `${path}.id`);
      ['x', 'y', 'width', 'height', 'rotation', 'opacity'].forEach(key => { if (!Number.isFinite(layer[key])) add(`${path}.${key}`, 'must be a number'); });
      if (layer.binding !== null && layer.binding !== undefined && !BINDINGS.includes(layer.binding)) add(`${path}.binding`, 'is unknown');
      if (layer.effects !== undefined && !Array.isArray(layer.effects)) add(`${path}.effects`, 'must be a list');
      if (Array.isArray(layer.effects)) {
        if (layer.effects.length > LIMITS.effects) add(`${path}.effects`, `holds more than ${LIMITS.effects} effects`);
        layer.effects.forEach((effect, e) => { if (!EFFECT_TYPES.includes(effect?.type)) add(`${path}.effects.${e}.type`, 'is unknown'); });
      }
      if (layer.type === 'text') {
        if (typeof layer.text !== 'string' || layer.text.length > LIMITS.textLength) add(`${path}.text`, 'must be text up to 2000 characters');
        if (!isColor(layer.style?.color)) add(`${path}.style.color`, 'must be #rrggbb');
      }
      const checkSource = (source, at) => {
        if (!IMAGE_SOURCES.includes(source?.kind)) add(`${at}.kind`, 'is unknown');
        if (source?.kind === 'asset' && !ID_PATTERN.asset.test(source.asset_id || '')) add(`${at}.asset_id`, 'must match sas-');
      };
      if (layer.type === 'shape' && !SHAPES.includes(layer.shape)) add(`${path}.shape`, 'is unknown');
      if (layer.type === 'shape' && layer.fill?.type === 'image') {
        if (layer.shape === 'line') add(`${path}.fill`, 'a line has no fill');
        checkSource(layer.fill.source, `${path}.fill.source`);
      }
      if (layer.type === 'text' && layer.style?.fill?.type === 'image') checkSource(layer.style.fill.source, `${path}.style.fill.source`);
      if (layer.type === 'text' && layer.style?.fill && !PAINT_TYPES.includes(layer.style.fill.type)) add(`${path}.style.fill.type`, 'is unknown');
      if (layer.type === 'image') checkSource(layer.source, `${path}.source`);
      if (layer.type === 'icon' && !isColor(layer.color)) add(`${path}.color`, 'must be #rrggbb');
      if (layer.type === 'video') validateVideo(layer, path, doc.format, add);
    });
  });
  return errors;
}
